import { useEffect, useState, useRef } from "react";
import * as React from "react";
import { Game } from "./lib/Game";
import { RemoteInterface } from "~lib/RemoteInterface";
import { format } from "date-fns";
import { UpdateType } from "../../backend/src/lib/types";
import { Background } from "~lib/Background";

type Event = {
  time: Date;
  text: string;
  colour?: string;
};

export type ScoreboardEntry = {
  name: string;
  colour: string;
  kills: number;
  deaths: number;
};

export default () => {
  const [lobby, setLobby] = useState<string>("");
  const [ready, setReady] = useState<boolean>(false);
  const [events, setEvents] = useState<Event[]>([]);
  const [scoreboard, setScoreboard] = useState<ScoreboardEntry[]>([]);
  const game = useRef<Game>();
  const codeInput = useRef<HTMLInputElement>(null);
  const nameInput = useRef<HTMLInputElement>(null);
  const chatInput = useRef<HTMLInputElement>(null);
  const colourInput = useRef<HTMLInputElement>(null);
  const INITIAL_COLOUR_POOL: string[] = [
    "#FF0000",
    "#00FF00",
    "#0000FF",
    "#FFFF00",
    "#FF7700",
    "#77FF00",
    "#FF00FF",
    "#FF0077",
    "#7700FF",
    "#00FFFF",
    "#0077FF",
    "#00FF77",
  ];

  const pushEvent = (text: string, colour?: string) => {
    setEvents((events) => [
      { time: new Date(), text: text, colour: colour },
      ...events.slice(Math.max(0, events.length - 100), 100),
    ]);
  };

  const onDisconnect = () => {
    setLobby("");
    setScoreboard([]);
  };

  useEffect(() => {
    game.current = new Game(
      document.querySelector("#main")!,
      pushEvent,
      onDisconnect,
      setScoreboard
    );
    const timer = setInterval(
      game.current.onTick.bind(game),
      RemoteInterface.TICK_MS / 5
    );
    setReady(true);
    return () => {
      clearInterval(timer);
    };
  }, []);

  const joinGame = async (code: string = "") => {
    await game.current!.init(
      code,
      nameInput.current!.value,
      colourInput.current!.value
    );
    setLobby(game.current!.engine!.remote.room);
  };

  const sendChat = () => {
    if (!chatInput.current?.value) return;
    game.current?.engine?.remote.sendUpdate({
      type: UpdateType.CHAT,
      text: chatInput.current.value,
      pid: game.current?.engine?.localPlayerId,
    });
    pushEvent(
      `${game.current?.engine?.getLocalPlayer().name}: ${
        chatInput.current.value
      }`,
      game.current?.engine?.getLocalPlayer().colour
    );
    chatInput.current.value = "";
  };

  return (
    <div
      style={{
        marginLeft: 8,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <h1>ASD</h1>
      <div
        style={{
          display: "flex",
          gap: 16,
        }}
      >
        <canvas
          id="main"
          width={Background.WIDTH}
          height={Background.HEIGHT + 120}
        ></canvas>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <details
            style={{ border: "1px solid black", padding: 8 }}
            open={true}
          >
            <summary>
              <strong>How to play</strong>
            </summary>
            <ul>
              <li>Move with A/D</li>
              <li>Adjust weapon angle with Q/E</li>
              <li>Adjust weapon power with W/S</li>
              <li>Fire weapon with Space</li>
              <li>Change weapon with Z/X</li>
            </ul>
          </details>
          <div style={{ border: "1px solid black", padding: 8 }}>
            <span>Current room: {lobby ? lobby : "not connected"}</span>
            <button
              style={{ marginLeft: 8 }}
              onClick={() => game.current!.engine!.remote.ws.close()}
              disabled={lobby === ""}
            >
              Leave
            </button>
            <hr />
            <div style={{ display: "flex" }}>
              <span style={{ flexBasis: 120 }}>Player name:</span>
              <input
                ref={nameInput}
                style={{ marginLeft: 8 }}
                disabled={lobby !== ""}
                defaultValue="player"
              />
            </div>
            <div style={{ display: "flex" }}>
              <span style={{ flexBasis: 120 }}>Player colour:</span>
              <input
                ref={colourInput}
                style={{ marginLeft: 8 }}
                type="color"
                defaultValue={
                  INITIAL_COLOUR_POOL[
                    Math.floor(Math.random() * INITIAL_COLOUR_POOL.length)
                  ]
                }
                disabled={lobby !== ""}
              />
            </div>
            <hr />
            <button
              style={{ marginBottom: 8 }}
              onClick={() => joinGame()}
              disabled={!ready || lobby !== ""}
            >
              Create game
            </button>
            <div>
              <span>Room code:</span>
              <input
                ref={codeInput}
                style={{ marginRight: 8, marginLeft: 8 }}
                disabled={!ready || lobby !== ""}
              />
              <button
                style={{ display: "inline" }}
                onClick={() =>
                  joinGame(codeInput.current?.value.toUpperCase().trim())
                }
                disabled={!ready || lobby !== ""}
              >
                Join game
              </button>
            </div>
          </div>
          <div style={{ border: "1px solid black", padding: 8, flexGrow: 1 }}>
            <h3 style={{ marginTop: 0 }}>Scoreboard</h3>
            {lobby === "" ? (
              <p>Create or join a game!</p>
            ) : (
              <table style={{ width: "100%" }}>
                <thead>
                  <tr style={{ textAlign: "left" }}>
                    <th></th>
                    <th style={{ paddingLeft: 8 }}>Player</th>
                    <th>Kills</th>
                    <th>Deaths</th>
                  </tr>
                </thead>
                <tbody>
                  {scoreboard.map((x) => (
                    <tr>
                      <td style={{ backgroundColor: x.colour, width: 20 }}></td>
                      <td style={{ paddingLeft: 8 }}>{x.name}</td>
                      <td>{x.kills}</td>
                      <td>{x.deaths}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
      <div
        style={{
          border: "1px solid black",
          padding: 8,
          width: Background.WIDTH - 16,
          marginTop: 16,
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <h3 style={{ marginTop: 0 }}>Game log</h3>
        <div style={{ display: "flex", fontSize: 20, gap: 8 }}>
          <input
            className="ignoreKeyEvents"
            ref={chatInput}
            style={{ width: "100%" }}
            disabled={lobby === ""}
            onKeyDown={(e) => {
              if (e.code === "Enter") sendChat();
            }}
          />
          <button disabled={lobby === ""} onClick={sendChat}>
            Send
          </button>
        </div>
        <div
          style={{
            overflow: "scroll",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {events.map((x) => (
            <p style={{ margin: 4, display: "flex", gap: 4 }}>
              <span>[{format(x.time, "HH:mm")}]</span>
              {x.colour && (
                <div
                  style={{
                    width: 20,
                    backgroundColor: x.colour,
                    display: "inline",
                  }}
                ></div>
              )}
              <span>{x.text}</span>
            </p>
          ))}
        </div>
      </div>
    </div>
  );
};
